import * as borogove from "borogove";
import { Link, Navigate, useOutletContext } from "react-router";
import { Context } from "../App";
import { useState } from "react";
import { Alert, CircularProgress } from "@mui/material";

const registerPush = (
	client: borogove.Client,
	idbp: Awaited<ReturnType<typeof borogove.persistence.IDB>>,
) => {
	if (!import.meta.env.VITE_PUSH_SERVICE) {
		console.warn("No WebPush service configured, skipping");
		return;
	}

	client.addStatusOnlineListener(() => {
		(async () => {
			if (!navigator.serviceWorker || !Notification?.permission) return; // If no service worker support, don't bother with push

			const serviceWorkerRegistration = await navigator.serviceWorker.ready;
			// public key can be computed from private key, but then we need code to do that, so we store both
			let key = (await idbp.get("vapid_key")) as {
				publicKey: ArrayBuffer;
				privateKey: ArrayBuffer;
			};
			let keyPair;
			if (key) {
				const publicKey = await window.crypto.subtle.importKey(
					"raw",
					key.publicKey,
					{
						name: "ECDSA",
						namedCurve: "P-256",
					},
					true,
					["verify"],
				);
				const privateKey = await window.crypto.subtle.importKey(
					"pkcs8",
					key.privateKey,
					{
						name: "ECDSA",
						namedCurve: "P-256",
					},
					true,
					["sign"],
				);
				keyPair = { publicKey, privateKey };
			} else {
				keyPair = await window.crypto.subtle.generateKey(
					{
						name: "ECDSA",
						namedCurve: "P-256",
					},
					true,
					["sign", "verify"],
				);
				key = {
					publicKey: await window.crypto.subtle.exportKey(
						"raw",
						keyPair.publicKey,
					),
					privateKey: await window.crypto.subtle.exportKey(
						"pkcs8",
						keyPair.privateKey,
					),
				};
				await idbp.set("vapid_key", key);
			}
			client.subscribePush(
				serviceWorkerRegistration,
				import.meta.env.VITE_PUSH_SERVICE,
				keyPair,
				144,
			);
		})();
	});
};

export const newAccount = (
	accountId: string,
	idbp: Awaited<ReturnType<typeof borogove.persistence.IDB>>,
	persistence: borogove.Persistence,
) => {
	const client = new borogove.Client(accountId, persistence);
	registerPush(client, idbp);
	setTimeout(() => client.start(), 0);

	return {
		client,
		status: "connecting",
	};
};

export const Login = () => {
	const { accounts, setAccounts, persistence, idbp } =
		useOutletContext<Context>();
	const [accountId, setAccountId] = useState(undefined);
	const [authAttempts, setAuthAttempts] = useState(0);

	const account = accountId ? accounts[accountId] : null;

	const setStatus = (status: string) => {
		if (!accountId) return;

		setAccounts({
			...accounts,
			[accountId]: { ...accounts[accountId], status },
		});
	};

	if (account?.status === "online") {
		return (
			<Navigate
				to={`/accounts/${encodeURIComponent(account.client.accountId()).replace(/%40/, "@")}`}
			/>
		);
	}

	const connectError = account?.status === "error";
	const needAuth = account?.status === "auth";
	return (
		<div id="login">
			<form
				onSubmit={(e) => {
					e.preventDefault();
					if (!account) {
						if (!e.currentTarget.jid.value) return;

						setAuthAttempts(0);
						const accountId = e.currentTarget.jid.value;
						setAccountId(accountId);
						if (!accounts[accountId]) {
							setAccounts({
								...accounts,
								[accountId]: newAccount(accountId, idbp, persistence),
							});
						}
					} else {
						setAuthAttempts(authAttempts + 1);
						account.client.usePassword(e.currentTarget.password.value);
						setStatus("connecting");
					}
				}}
			>
				<h1>Cheogram</h1>
				<p className="slogan">Connect to all your contacts</p>
				<input
					name="jid"
					type="text"
					placeholder="Jabber ID"
					value={account?.client?.accountId()}
					disabled={!!account && !connectError}
				/>
				{connectError && (
					<Alert severity="error">Server connection failed</Alert>
				)}
				{needAuth && (
					<input
						name="password"
						type="password"
						placeholder="Enter your password"
					/>
				)}
				{needAuth && authAttempts > 0 && (
					<Alert severity="error">Login failed. Please try again.</Alert>
				)}
				{!account || needAuth ? (
					<button>{account ? "Login" : "Next"}</button>
				) : (
					<CircularProgress />
				)}
				{!account && (
					<Link to="/register?with=jabber.fr">Don&#39;t have an account?</Link>
				)}
			</form>
		</div>
	);
};
